import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Truck } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { BOOKING_LINKS } from "../config";

const HERO_IMG = "/assets/folded-stack.webp";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const vanY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative mx-auto max-w-7xl px-6 pb-24 pt-40 sm:pt-48 lg:px-10 lg:pb-32"
    >
      <div className="grid items-center gap-16 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#E2DFD8] bg-white/60 px-4 py-2"
            data-testid="hero-eyebrow"
          >
            <MapPin className="h-3.5 w-3.5 text-[#C85C40]" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5A]">
              Bryans Road, Maryland
            </span>
          </motion.div>

          <h1 className="font-serif text-5xl font-bold leading-[0.95] tracking-tighter sm:text-7xl lg:text-[6rem]">
            <MaskedLine delay={0.1}>Wash. Dry.</MaskedLine>
            <MaskedLine delay={0.22}>Fold.</MaskedLine>
            <MaskedLine delay={0.34}>
              <span className="italic font-medium text-[#C85C40]">Relax.</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base leading-relaxed text-[#5C5C5A] md:text-lg"
          >
            Spintastic City Laundry Services is your neighborhood laundromat on
            Indian Head Highway — gleaming self-service machines, lovingly
            wash-dry-folded bundles, and pickup &amp; delivery that comes to you.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={BOOKING_LINKS.pickupDelivery}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-schedule-pickup-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-[#C85C40] px-8 py-4 text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:scale-[0.98] hover:bg-[#A84C34]"
            >
              Schedule Pickup &amp; Delivery
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#services"
              data-testid="hero-explore-services-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[#1A1A1A]/15 px-8 py-4 text-sm font-bold text-[#1A1A1A] transition-[background-color,border-color] duration-300 hover:border-[#1A1A1A] hover:bg-white"
            >
              Explore Services
            </a>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-[3rem] shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
            data-testid="hero-image-frame"
          >
            <motion.img
              src={HERO_IMG}
              alt="Arms carrying a tall stack of freshly folded, cozy knit laundry"
              style={{ y: imgY, scale: 1.15 }}
              className="aspect-[4/5] w-full object-cover will-change-transform"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -bottom-6 -left-4 flex items-center gap-4 rounded-[24px] border border-[#E2DFD8]/60 bg-white/90 p-5 shadow-[0_4px_24px_rgba(0,0,0,0.06)] backdrop-blur-md sm:-left-10"
            data-testid="hero-delivery-badge"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#7D8C74]/15 text-[#7D8C74]">
              <Truck className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-bold">Pickup &amp; Delivery</p>
              <p className="text-xs text-[#5C5C5A]">Our van &amp; trusted gig drivers</p>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
        className="mt-20 overflow-hidden rounded-[3rem] shadow-[0_4px_24px_rgba(0,0,0,0.04)] lg:mt-24"
        data-testid="hero-van-frame"
      >
        <motion.img
          src="/assets/van-branded.png"
          alt="Spintastic City delivery van wrapped in brand colors with the company logo"
          style={{ y: vanY, scale: 1.12 }}
          className="aspect-[16/9] w-full object-cover will-change-transform sm:aspect-[21/9]"
        />
      </motion.div>
    </section>
  );
}
